import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { documentsApi } from "@/api/endpoints";
import type { InboundDocumentItem, InboundRoutingKpis, UnifiedDocument } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatDate } from "@/utils/format";

type Tab = "library" | "inbox";

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function statusClass(status: string): string {
  if (status === "auto_routed" || status === "routed") return "inbound-status-ok";
  if (status === "needs_review" || status === "pending") return "inbound-status-warn";
  return "inbound-status-bad";
}

function labelize(value: string): string {
  return value.replace(/_/g, " ");
}

export function DocumentsPage() {
  const [tab, setTab] = useState<Tab>("inbox");

  return (
    <>
      <PageHeader
        title="Documents"
        subtitle="Unified library and inbound carrier notice auto-routing."
      />
      <div className="tab-row" style={{ marginBottom: "1rem" }}>
        <button
          type="button"
          className={`tab-btn${tab === "inbox" ? " active" : ""}`}
          onClick={() => setTab("inbox")}
        >
          Inbound inbox
        </button>
        <button
          type="button"
          className={`tab-btn${tab === "library" ? " active" : ""}`}
          onClick={() => setTab("library")}
        >
          Library
        </button>
      </div>
      {tab === "inbox" ? <InboundInbox /> : <DocumentLibrary />}
    </>
  );
}

function DocumentLibrary() {
  const [scope, setScope] = useState<"all" | "client" | "claim">("all");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<UnifiedDocument[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    documentsApi
      .library({
        q: query || undefined,
        scope: scope === "all" ? undefined : scope,
      })
      .then((data) => setItems(data.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load documents"));
  }, [query, scope]);

  return (
    <>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {(["all", "client", "claim"] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`btn ${scope === value ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setScope(value)}
          >
            {value === "all" ? "All scopes" : value === "client" ? "Client docs" : "Claim evidence"}
          </button>
        ))}
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search filename, client, or claim ref…"
        style={{ maxWidth: 360, marginBottom: "0.75rem" }}
      />
      {error ? <div className="error-banner">{error}</div> : null}
      <div className="card">
        {items.length === 0 ? (
          <div className="empty-state">No documents match this filter.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Scope</th>
                <th>Linked record</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr key={`${doc.scope}-${doc.id}`}>
                  <td>
                    <strong>{doc.original_filename}</strong>
                    {doc.description ? (
                      <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{doc.description}</div>
                    ) : null}
                  </td>
                  <td>{doc.scope}</td>
                  <td>
                    {doc.customer_id ? (
                      <Link to={`/clients/${doc.customer_id}`}>{doc.customer_name ?? "Client"}</Link>
                    ) : null}
                    {doc.claim_id ? (
                      <Link to={`/claims/${doc.claim_id}`}>{doc.claim_ref ?? doc.claim_id.slice(0, 8)}</Link>
                    ) : null}
                    {!doc.customer_id && !doc.claim_id ? "—" : null}
                  </td>
                  <td>{doc.document_type}</td>
                  <td>{formatBytes(doc.size_bytes)}</td>
                  <td>{formatDate(doc.uploaded_at)}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      {doc.download_url ? (
                        <a href={doc.download_url} className="btn btn-secondary" target="_blank" rel="noreferrer">
                          Download
                        </a>
                      ) : null}
                      {doc.policy_id ? (
                        <Link to={`/policies/${doc.policy_id}`} className="btn btn-secondary">
                          Policy
                        </Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function InboundInbox() {
  const [kpis, setKpis] = useState<InboundRoutingKpis | null>(null);
  const [items, setItems] = useState<InboundDocumentItem[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [fromAddress, setFromAddress] = useState("notices@carrier.example");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [routeCustomerId, setRouteCustomerId] = useState<Record<string, string>>({});

  const reload = useCallback(() => {
    Promise.all([documentsApi.inboundKpis(), documentsApi.listInbound(filter || undefined)])
      .then(([kpiData, listData]) => {
        setKpis(kpiData);
        setItems(listData.items);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load inbox"));
  }, [filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onUploadFile(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError("Choose a file to upload.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (subject.trim()) formData.append("subject", subject.trim());
      if (fromAddress.trim()) formData.append("from_address", fromAddress.trim());
      await documentsApi.uploadInbound(formData);
      setFile(null);
      setSubject("");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function onIngestEmail(event: FormEvent) {
    event.preventDefault();
    if (!subject.trim()) {
      setError("Subject is required for email ingest.");
      return;
    }
    setUploading(true);
    try {
      await documentsApi.ingestEmail({
        from_address: fromAddress.trim() || "unknown@carrier.example",
        subject: subject.trim(),
        body: body.trim(),
      });
      setSubject("");
      setBody("");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Email ingest failed");
    } finally {
      setUploading(false);
    }
  }

  async function onRoute(item: InboundDocumentItem) {
    const customerId = routeCustomerId[item.id] || item.customer_id;
    if (!customerId) {
      setError("Enter a customer ID to route this document.");
      return;
    }
    setBusyId(item.id);
    try {
      await documentsApi.routeInbound(item.id, {
        customer_id: customerId,
        policy_id: item.policy_id ?? undefined,
        claim_id: item.claim_id ?? undefined,
        notes: "Manually confirmed from inbound inbox",
      });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Route failed");
    } finally {
      setBusyId(null);
    }
  }

  async function onReject(item: InboundDocumentItem) {
    setBusyId(item.id);
    try {
      await documentsApi.rejectInbound(item.id, "Rejected from inbound inbox");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {kpis ? (
        <section className="stat-grid">
          <StatCard label="Needs review" value={kpis.needs_review} />
          <StatCard label="Auto-routed" value={kpis.auto_routed} />
          <StatCard label="Manually routed" value={kpis.routed} />
          <StatCard label="Rejected" value={kpis.rejected} />
        </section>
      ) : null}

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="detail-grid">
        <div className="card" style={{ padding: "1rem" }}>
          <h3 style={{ marginTop: 0 }}>Upload carrier notice</h3>
          <form onSubmit={onUploadFile} style={{ display: "grid", gap: "0.75rem" }}>
            <label>
              <div className="label">From</div>
              <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} />
            </label>
            <label>
              <div className="label">Subject</div>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Renewal notice — Policy POL-12345"
              />
            </label>
            <label>
              <div className="label">File</div>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? "Processing…" : "Upload & classify"}
            </button>
          </form>
        </div>

        <div className="card" style={{ padding: "1rem" }}>
          <h3 style={{ marginTop: 0 }}>Simulate inbound email</h3>
          <form onSubmit={onIngestEmail} style={{ display: "grid", gap: "0.75rem" }}>
            <label>
              <div className="label">From</div>
              <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} />
            </label>
            <label>
              <div className="label">Subject</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} required />
            </label>
            <label>
              <div className="label">Body</div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                placeholder="Include policy number, claim ref, or client email for auto-routing."
              />
            </label>
            <button type="submit" className="btn" disabled={uploading}>
              {uploading ? "Processing…" : "Ingest email"}
            </button>
          </form>
        </div>
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <h3 style={{ marginTop: 0 }}>Routing queue</h3>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[
              ["", "Open"],
              ["needs_review", "Needs review"],
              ["auto_routed", "Auto-routed"],
            ].map(([value, label]) => (
              <button
                key={value || "open"}
                type="button"
                className={`btn ${filter === value ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="empty-state">No inbound documents in this filter.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Notice</th>
                <th>Type</th>
                <th>Status</th>
                <th>Suggested links</th>
                <th>Confidence</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.subject || item.original_filename}</strong>
                    <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                      {item.from_address ?? "—"} · {item.source} · {formatDate(item.created_at)}
                    </div>
                    {item.routing_rationale ? (
                      <div style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>{item.routing_rationale}</div>
                    ) : null}
                  </td>
                  <td>{labelize(item.notice_type)}</td>
                  <td>
                    <span className={`inbound-status-badge ${statusClass(item.routing_status)}`}>
                      {labelize(item.routing_status)}
                    </span>
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>
                    {item.customer_id ? (
                      <div>
                        Client: <Link to={`/clients/${item.customer_id}`}>{item.customer_id.slice(0, 8)}</Link>
                      </div>
                    ) : (
                      <div style={{ color: "var(--muted)" }}>No client match</div>
                    )}
                    {item.policy_id ? (
                      <div>
                        Policy: <Link to={`/policies/${item.policy_id}`}>{item.policy_id.slice(0, 8)}</Link>
                      </div>
                    ) : null}
                    {item.claim_id ? (
                      <div>
                        Claim: <Link to={`/claims/${item.claim_id}`}>{item.claim_id.slice(0, 8)}</Link>
                      </div>
                    ) : null}
                    {item.routing_status === "needs_review" && !item.customer_id ? (
                      <input
                        placeholder="Customer UUID"
                        value={routeCustomerId[item.id] ?? ""}
                        onChange={(e) =>
                          setRouteCustomerId((prev) => ({ ...prev, [item.id]: e.target.value }))
                        }
                        style={{ marginTop: "0.35rem", maxWidth: "14rem" }}
                      />
                    ) : null}
                  </td>
                  <td>
                    {item.classification_confidence != null
                      ? `${(item.classification_confidence * 100).toFixed(0)}%`
                      : "—"}
                  </td>
                  <td>
                    {item.routing_status === "needs_review" || item.routing_status === "pending" ? (
                      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busyId === item.id || !(routeCustomerId[item.id] || item.customer_id)}
                          onClick={() => onRoute(item)}
                        >
                          {busyId === item.id ? "…" : "Attach"}
                        </button>
                        <button
                          type="button"
                          className="btn"
                          disabled={busyId === item.id}
                          onClick={() => onReject(item)}
                        >
                          Reject
                        </button>
                      </div>
                    ) : item.customer_document_id && item.customer_id ? (
                      <Link to={`/clients/${item.customer_id}`}>View client docs</Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
